// @ts-strict-ignore
import {Component, computed, effect, ElementRef, inject, input, output, viewChild, viewChildren} from "@angular/core";
import {Translations} from "@translations";
import {VMEventDay} from "@models";
import {BehaviorSubject} from "rxjs";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {MatTooltip} from "@angular/material/tooltip";
import {DatePipe, KeyValuePipe} from "@angular/common";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatBadge} from "@angular/material/badge";
import {ChevronOverflowComponent} from "@common/ui/chevron-overflow/chevron-overflow.component";

@UntilDestroy()
@Component({
	selector: "sim-event-day-selector",
	templateUrl: "./event-day-selector.component.html",
	styleUrl: "./event-day-selector.component.scss",
	imports: [MatTooltip, DatePipe, KeyValuePipe, TranslateModule, MatBadge, ChevronOverflowComponent],
})
export class EventDaySelectorComponent {
	readonly translate = inject(TranslateService);
	// I/O
	public readonly eventDays = input.required<VMEventDay[]>();
	public readonly allowSelectEmptyDay = input<boolean>(false);
	public readonly onSelect = output<VMEventDay>();

	// Properties
	public daysByMonth = computed<Map<Date, VMEventDay[]>>(() => this._computeDaysByMonth());
	public $selectedDay = new BehaviorSubject<VMEventDay | null>(null);

	// ViewChildren
	public months = viewChildren<ElementRef>("monthElement");
	public overflowHandler = viewChild.required<ChevronOverflowComponent>("overflowHandler");

	// Readonly
	protected readonly T = Translations;

	constructor() {
		effect(() => this._effectSelectInitialDay());
		this.$selectedDay.pipe(untilDestroyed(this)).subscribe((value) => this.onSelect.emit(value));
	}

	protected isSameDay(dateA: string | Date, dateB: string | Date): boolean {
		const convertedDateA = new Date(dateA);
		const convertedDateB = new Date(dateB);
		return (
			convertedDateA.getFullYear() === convertedDateB.getFullYear() &&
			convertedDateA.getMonth() === convertedDateB.getMonth() &&
			convertedDateA.getDate() === convertedDateB.getDate()
		);
	}

	public checkOverflow(): void {
		this.overflowHandler().checkOverflow();
	}

	private _effectSelectInitialDay(): void {
		// Prep & validate
		const days = this.daysByMonth();
		if (!days.size) {
			return;
		}

		// Set the first day as current
		const firstMonth = Array.from(days.keys()).sort()[0];
		const firstDay = days.get(firstMonth)[0];
		this.$selectedDay.next(firstDay);
	}

	private _computeDaysByMonth(): Map<Date, VMEventDay[]> {
		// Prepare & validate
		const temporaryMap = new Map<string, VMEventDay[]>();
		const days = this.eventDays();
		if (!days) {
			return new Map<Date, VMEventDay[]>();
		}

		// Group days by a temporary key (comparison doesn't work with a Date object)
		for (const day of days) {
			const dayDate = new Date(day.date);
			const temporaryKey = `${dayDate.getFullYear()}-${dayDate.getMonth()}`;

			if (temporaryMap.has(temporaryKey)) {
				temporaryMap.get(temporaryKey).push(day);
			} else {
				temporaryMap.set(temporaryKey, [day]);
			}
		}

		// Convert the keys back to Date objects
		const result = new Map<Date, VMEventDay[]>();
		for (const [key, value] of temporaryMap.entries()) {
			const [year, month] = key.split("-").map(Number);
			const dateKey = new Date(year, month);
			result.set(dateKey, value);
		}
		return result;
	}
}
